import React, { useEffect, useMemo, useState } from 'react'
import ContentWrapper from '../../components/contentwrapper/ContentWrapper'
import { Helmet } from 'react-helmet'
import withAuth from '../../utils/withAuth'
import { addFormData, googleAdsConnectUrl, googleAdsFetchMetricsAPI, googleAdsListAccountsAPI, googleListTenantsByPpcAPI } from '../../utils/api'
import { Button, Col, Form, Modal } from 'react-bootstrap'
import { toast as notify } from "react-toastify";
import { FaRegHandPointRight } from 'react-icons/fa'


const Pill = ({ ok, label }) => (
    <span className={`gi-pill ml-2 ${ok ? 'ok' : 'bad'}`}>{label}</span>
)

const GoogleAuth = () => {

    const [validated, setValidated] = useState(false);
    const ppcid = localStorage.getItem("ppcdata");

    const [tenantId, setTenantId] = useState('');           // selected tenant
    const [tenants, setTenants] = useState([]);           // dropdown list

    const [connected, setConnected] = useState(false)
    const [accounts, setAccounts] = useState([])
    const [selectedAccount, setSelectedAccount] = useState('')
    const [metrics, setMetrics] = useState([])
    const [loadingAccounts, setLoadingAccounts] = useState(false)
    const [loadingMetrics, setLoadingMetrics] = useState(false)
    const [error, setError] = useState('')
    const [activeTab, setActiveTab] = useState('accounts') // accounts | spend
    const [lastSync, setLastSync] = useState(null)

    // default dates: last 7 days
    const todayStr = () => new Date().toISOString().slice(0, 10)
    const sevenDaysAgoStr = () => {
        const d = new Date()
        d.setDate(d.getDate() - 7)
        return d.toISOString().slice(0, 10)
    }
    const [fromDate, setFromDate] = useState(sevenDaysAgoStr())
    const [toDate, setToDate] = useState(todayStr())

    const numberWithCommas = (x) =>
        x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') ?? '0'

    const totals = useMemo(() => {
        const t = metrics.reduce(
            (acc, r) => {
                acc.impr += Number(r.impr || 0)
                acc.clicks += Number(r.clicks || 0)
                acc.costMicros += Number(r.costMicros || 0)
                return acc
            },
            { impr: 0, clicks: 0, costMicros: 0 }
        )
        return {
            impr: t.impr,
            clicks: t.clicks,
            cost: (t.costMicros / 1_000_000).toFixed(2),
        }
    }, [metrics])

    const fetchTenants = async () => {
        if (!ppcid) return;
        try {
            const res = await googleListTenantsByPpcAPI(ppcid);
            const list = Array.isArray(res?.data) ? res.data : [];
            setTenants(list);
            if (list.length && !tenantId) {
                setTenantId(String(list[0].id));   // default select first tenant
            }
        } catch (e) {
            // silent; you can surface a toast if you want
        }
    };

    const fetchAccounts = async () => {
        if (!tenantId) return;
        setLoadingAccounts(true);
        setError('');
        try {
            const resAcc = await googleAdsListAccountsAPI(tenantId); // <-- tenantId here
            if (!resAcc || !resAcc.data || (resAcc.status && resAcc.status >= 400)) {
                const msg = resAcc?.response?.data?.message || 'Not connected to Google Ads. Please connect first.';
                setConnected(false); setAccounts([]); setSelectedAccount(''); setError(msg);
            } else {
                const list = Array.isArray(resAcc.data) ? resAcc.data : [];
                setAccounts(list);
                if (list.length > 0) {
                    setSelectedAccount(String(list[0].customer_id));
                    setConnected(true);
                } else {
                    setConnected(true);
                }
            }
        } catch (e) {
            setConnected(false); setAccounts([]); setSelectedAccount('');
            setError(e?.response?.data?.message || 'Not connected to Google Ads. Please connect first.');
        } finally {
            setLoadingAccounts(false);
        }
    };

    const fetchMetrics = async () => {
        if (!tenantId || !selectedAccount) return;
        if (fromDate > toDate) { setError('From date cannot be after To date.'); return; }

        setLoadingMetrics(true); setError(''); setMetrics([]);
        try {
            const resMet = await googleAdsFetchMetricsAPI({
                tenant_id: tenantId,                   // <-- tenantId here
                customer_id: selectedAccount,
                from: fromDate, to: toDate,
            });
            if (!resMet || !resMet.data || (resMet.status && resMet.status >= 400)) {
                const msg = resMet?.response?.data?.message || 'Failed to fetch metrics. Check dates/account and try again.';
                setError(msg); setMetrics([]);
            } else {
                setMetrics(Array.isArray(resMet.data) ? resMet.data : []);
                setActiveTab('spend');
                setLastSync(new Date().toISOString().replace('T', ' ').slice(0, 19));
            }
        } catch (e) {
            setError(e?.response?.data?.message || 'Failed to fetch metrics. Check dates/account and try again.');
        } finally {
            setLoadingMetrics(false);
        }
    };

    const connectHref = tenantId ? googleAdsConnectUrl(tenantId) : '#';

    useEffect(() => { fetchTenants(); }, []);
    useEffect(() => { if (tenantId) fetchAccounts(); }, [tenantId]);

    const downloadCsv = () => {
        if (!metrics.length) return
        const headers = [
            'Date',
            'CampaignID',
            'AdGroupID',
            'AdID',
            'Impressions',
            'Clicks',
            'Spend',
        ]
        const rows = metrics.map((r) => [
            r.date,
            r.campaignId,
            r.adGroupId,
            r.adId,
            r.impr,
            r.clicks,
            (Number(r.costMicros || 0) / 1_000_000).toFixed(2),
        ])
        const csv =
            headers.join(',') +
            '\n' +
            rows
                .map((row) =>
                    row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')
                )
                .join('\n')

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `google_ads_spend_${fromDate}_to_${toDate}.csv`
        a.click()
        URL.revokeObjectURL(url)
    }

    useEffect(() => {
        fetchAccounts()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    const [values, setInputValue] = useState();
    const [tenantshow, setTenantShow] = useState(false);

    const handleTenantClose = () => setTenantShow(false);

    const habdleTenant = () => {
        setTenantShow(true);
    }
    const handleInputs = e => {
        setInputValue({
            ...values,
            [e.target.name]: e.target.value,
        });
    };

    const handleTenantSubmit = async (event) => {
        event.preventDefault();
        const form = event.currentTarget;

        if (!form.checkValidity()) {
            event.stopPropagation();
            setValidated(true);
            return;
        }

        try {
            const payload = {
                name: values.tenant_name?.trim(),
                ppc_id: ppcid,
            };
            const res = await addFormData("ppc-add-google-tenant-name", payload);
            const status = res?.data?.status;

            if (status === 1) {
                notify.success("Tenant has been added successfully!!");
            }
            else if (status === 2) notify.warning("Tenant already exists!!");
            else notify.error("Something went wrong!!");

            setTenantShow(false);
        } catch {
            notify.error("Something went wrong!!");
        }
        setValidated(true);
    };
    return (
        <div className="misDashboard fb-surface">
            <Helmet>
                <title>SATKARTAR :: Google Integration</title>
            </Helmet>

            <ContentWrapper>
                <Modal show={tenantshow} onHide={handleTenantClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>ADD TENANT</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form noValidate validated={validated} onSubmit={handleTenantSubmit} method="post">
                            <Form.Group as={Col} md="12" controlId="validationCustom01">
                                <Form.Label>Enter Tenant Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Tenant Name"
                                    name="tenant_name"
                                    onChange={handleInputs}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    <FaRegHandPointRight /> Please provide a valid Tenant Name
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Button type="submit" className="btn btn-sm" style={{ display: "flex", margin: "auto", marginTop: "10px" }}>Submit</Button>
                        </Form>
                    </Modal.Body>
                </Modal>
                {/* Hero header like Facebook Integration */}
                <div className="gi-hero d-flex align-items-center justify-content-between mb-3">
                    <div className="text-black">
                        <h3 className="mb-1">Google Ads Integration</h3>
                        <div className="fb-hero-sub">
                            Connect accounts, pull ad spend, and view insights.
                        </div>
                    </div>
                    <div className="d-flex">
                        <Button variant="outline-primary" className="fb-rebtn" onClick={fetchAccounts}>
                            Refresh
                        </Button>
                        <a href={connectHref} className="btn btn-googleconnect">
                            {connected ? 'Reconnect' : 'Connect Google Ads'}
                        </a>
                        <Button variant="secondary" className="fb-btn" onClick={habdleTenant}>
                            ADD TENANT
                        </Button>
                    </div>
                </div>

                {error && <div className="alert alert-danger py-2">{error}</div>}

                <div className="row">
                    {/* LEFT: Control Panel */}
                    <div className="col-lg-4 mb-3">
                        <div className="card gi-panel h-100 p-3">
                            <div className="card-body">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <h5 className="mb-0">Control Panel</h5>
                                </div>

                                {/* Tenant (static for now) */}
                                <div className="form-group">
                                    <label className="font-weight-bold">Tenant</label>
                                    <select
                                        className="form-control"
                                        value={tenantId}
                                        onChange={(e) => setTenantId(e.target.value)}
                                    >
                                        {tenants.map(t => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                        {!tenants.length && <option value="">No tenants yet</option>}
                                    </select>
                                    {!tenants.length && (
                                        <small className="text-muted">Add a tenant to proceed.</small>
                                    )}
                                </div>

                                {/* Status row */}
                                <div className="mb-2">
                                    <Pill ok={connected} label={connected ? 'OAuth OK' : 'OAuth Off'} />
                                    <Pill ok={true} label="MCC Set" />
                                </div>

                                <div className="text-muted small mb-3">
                                    Last sync: {lastSync ? lastSync : '—'}
                                </div>

                                {/* Tiles */}
                                <div className="d-flex">
                                    <div className="gi-stat mr-2">
                                        <div className="k">Ad Accounts</div>
                                        <div className="v">{accounts?.length || 0}</div>
                                    </div>
                                    <div className="gi-stat mr-2">
                                        <div className="k">Total Clicks</div>
                                        <div className="v">{numberWithCommas(totals.clicks)}</div>
                                    </div>
                                    <div className="gi-stat">
                                        <div className="k">Spend (₹)</div>
                                        <div className="v">{numberWithCommas(totals.cost)}</div>
                                    </div>
                                </div>

                                <div className="mt-3">
                                    <a href={connectHref} className="btn btn-primary btn-block">
                                        {connected ? 'Connect / Reconnect' : 'Connect Google Ads'}
                                    </a>
                                    <button
                                        className="btn btn-outline-primary btn-block mt-2"
                                        onClick={fetchAccounts}
                                    >
                                        Refresh Accounts
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: Tabs + content */}
                    <div className="col-lg-8 mb-3">
                        <div className="card h-100 p-3">
                            <div className="card-header p-0">
                                <ul className="nav nav-tabs gi-tabs">
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'accounts' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('accounts')}
                                        >
                                            Ad Accounts
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'spend' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('spend')}
                                            disabled={!connected}
                                        >
                                            Spend
                                        </button>
                                    </li>
                                </ul>
                            </div>

                            <div className="card-body">
                                {activeTab === 'accounts' && (
                                    <>
                                        <div className="d-flex mb-3">
                                            <div className="input-group">
                                                <div className="input-group-prepend">
                                                    <span className="input-group-text">🔎</span>
                                                </div>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search accounts by name or ID…"
                                                    onChange={(e) => {
                                                        const q = e.target.value.toLowerCase()
                                                        const base = accounts || []
                                                        const filtered = base.filter(
                                                            a =>
                                                                (a.name || '')
                                                                    .toLowerCase()
                                                                    .includes(q) || String(a.customer_id).includes(q)
                                                        )
                                                        setAccounts(q ? filtered : base)
                                                    }}
                                                />
                                            </div>
                                            <button
                                                className="btn btn-outline-secondary ml-2"
                                                onClick={fetchAccounts}
                                            >
                                                Refresh
                                            </button>
                                        </div>

                                        {loadingAccounts ? (
                                            <div className="d-flex align-items-center">
                                                <div className="spinner-border spinner-border-sm mr-2" />
                                                Loading accounts…
                                            </div>
                                        ) : accounts.length ? (
                                            <div className="list-group gi-accounts">
                                                {accounts.map((acc) => (
                                                    <div
                                                        key={acc.customer_id}
                                                        className={`list-group-item gi-account ${String(selectedAccount) === String(acc.customer_id) ? 'active' : ''
                                                            }`}
                                                        onClick={() => setSelectedAccount(String(acc.customer_id))}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <div className="gi-name">{acc.name || 'Unnamed Account'}</div>
                                                                <div className="gi-sub small text-muted">
                                                                    {acc.customer_id} • {acc.currency || '—'}
                                                                </div>
                                                            </div>
                                                            <button
                                                                className="btn btn-sm btn-success"
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    setSelectedAccount(String(acc.customer_id))
                                                                    setActiveTab('spend')
                                                                }}
                                                            >
                                                                View Spend
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="alert alert-light border">
                                                <div className="mb-1"><strong>No child accounts found.</strong></div>
                                                <div className="small text-muted">
                                                    Tip: Ensure your MCC has at least one linked client account.
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {activeTab === 'spend' && (
                                    <>
                                        {/* Filters bar (like FB) */}
                                        <div className="row mb-3">
                                            <div className="col-md-6">
                                                <label className="font-weight-bold">Select Ad Account</label>
                                                <select
                                                    className="form-control"
                                                    value={selectedAccount}
                                                    onChange={(e) => setSelectedAccount(e.target.value)}
                                                >
                                                    {accounts.map((acc) => (
                                                        <option key={acc.customer_id} value={acc.customer_id}>
                                                            {acc.name} ({acc.customer_id})
                                                        </option>
                                                    ))}
                                                    {!accounts.length && (
                                                        <option value="">No child accounts found</option>
                                                    )}
                                                </select>
                                            </div>
                                            <div className="col-md-3">
                                                <label className="font-weight-bold">From</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={fromDate}
                                                    onChange={(e) => setFromDate(e.target.value)}
                                                    max={toDate}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <label className="font-weight-bold">To</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={toDate}
                                                    onChange={(e) => setToDate(e.target.value)}
                                                    min={fromDate}
                                                    max={todayStr()}
                                                />
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <button
                                                className="btn btn-success"
                                                onClick={fetchMetrics}
                                                disabled={!selectedAccount}
                                            >
                                                {loadingMetrics ? 'Fetching…' : 'Fetch Spend'}
                                            </button>
                                            <button
                                                className="btn btn-outline-secondary ml-2"
                                                onClick={downloadCsv}
                                                disabled={!metrics.length}
                                            >
                                                Download CSV
                                            </button>
                                        </div>

                                        {/* Totals row */}
                                        <div className="row">
                                            <div className="col-md-4 mb-3">
                                                <div className="card gi-kpi">
                                                    <div className="card-body">
                                                        <div className="text-muted">Total Impressions</div>
                                                        <div className="h4 mb-0">{numberWithCommas(totals.impr)}</div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4 mb-3">
                                                <div className="card gi-kpi">
                                                    <div className="card-body">
                                                        <div className="text-muted">Total Clicks</div>
                                                        <div className="h4 mb-0">{numberWithCommas(totals.clicks)}</div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4 mb-3">
                                                <div className="card gi-kpi">
                                                    <div className="card-body">
                                                        <div className="text-muted">Total Spend</div>
                                                        <div className="h4 mb-0">₹ {numberWithCommas(totals.cost)}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Table */}
                                        <div className="card">
                                            <div className="card-header py-2">
                                                <strong>Per-Ad Spend</strong>
                                                <span className="text-muted ml-2">
                                                    ({fromDate} → {toDate})
                                                </span>
                                            </div>
                                            <div className="card-body p-0">
                                                {loadingMetrics ? (
                                                    <div className="p-3 d-flex align-items-center">
                                                        <div className="spinner-border spinner-border-sm mr-2" role="status" />
                                                        <span>Loading metrics…</span>
                                                    </div>
                                                ) : metrics.length ? (
                                                    <div className="table-responsive">
                                                        <table className="table table-striped table-bordered table-sm mb-0">
                                                            <thead className="thead-light">
                                                                <tr>
                                                                    <th style={{ whiteSpace: 'nowrap' }}>Date</th>
                                                                    <th>Campaign ID</th>
                                                                    <th>Ad Group ID</th>
                                                                    <th>Ad ID</th>
                                                                    <th className="text-right">Impressions</th>
                                                                    <th className="text-right">Clicks</th>
                                                                    <th className="text-right">Spend (₹)</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {metrics.map((r, i) => (
                                                                    <tr key={`${r.date}-${r.adId}-${i}`}>
                                                                        <td style={{ whiteSpace: 'nowrap' }}>{r.date}</td>
                                                                        <td>{r.campaignId}</td>
                                                                        <td>{r.adGroupId}</td>
                                                                        <td>{r.adId}</td>
                                                                        <td className="text-right">{numberWithCommas(r.impr)}</td>
                                                                        <td className="text-right">{numberWithCommas(r.clicks)}</td>
                                                                        <td className="text-right">
                                                                            {numberWithCommas(
                                                                                (Number(r.costMicros || 0) / 1_000_000).toFixed(2)
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <div className="p-3 text-muted">
                                                        No data yet. Choose an account and date range, then click “Fetch Spend”.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    )
}

export default withAuth(GoogleAuth)
